/* eslint-disable no-unused-vars */ // Contains variables for debugging/future features
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import featuredSeed from '../data/minted_featured.json';

// Cache-buster helper to avoid Cloudflare edge caching stale/empty responses
const ts = () => `ts=${Date.now()}`;

// Resilient JSON fetch with timeout and retries to handle intermittent edge/tunnel hiccups (with simple timing logs)
async function fetchJson(url, opts = {}) {
  const { retries = 2, timeoutMs = 6000, init } = opts;
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const t0 = performance.now();
      const ac = new AbortController();
      const tid = setTimeout(() => ac.abort(), timeoutMs);
      const res = await fetch(url, { ...(init || {}), signal: ac.signal });
      clearTimeout(tid);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      if (!text || !text.trim()) throw new Error('Empty body');
      const dj = JSON.parse(text);
      try { console.debug('[Immortalize] ok', url.split('?')[0], 'in', (performance.now()-t0|0), 'ms'); } catch {}
      return dj;
    } catch (e) {
      lastErr = e;
      try { console.warn('[Immortalize] fail', url.split('?')[0], String(e)); } catch {}
      await new Promise(r => setTimeout(r, 750));
    }
  }
  throw lastErr || new Error('fetch failed');
}

export default function Immortalize() {
  const [poll, setPoll] = useState(null);
  const [summary, setSummary] = useState(null);
  const [minted, setMinted] = useState([]);
  const [loading, setLoading] = useState(true); // page-level first paint
  const [error, setError] = useState('');       // soft errors for poll area only
  const [prefetch, setPrefetch] = useState({}); // scene_id -> evidence (preview posts only)
  const [showFull, setShowFull] = useState({}); // scene_id -> bool
  const [mode, setMode] = useState('scenes'); // 'scenes' | 'posts' (posts later)
  const [avatars, setAvatars] = useState({}); // name-key -> url
  const [postCandidates, setPostCandidates] = useState([]); // top posts view

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // Load poll data, but do not block minted rendering if it fails
        try {
          const latestUrl = `${API_BASE_URL}/api/archive/polls_latest?${ts()}`;
          const latest = await fetchJson(latestUrl);
          if (alive && latest && latest.poll_id) {
            const pollUrl = `${API_BASE_URL}/api/archive/polls/${latest.poll_id}?${ts()}`;
            const p = await fetchJson(pollUrl);
            setPoll(p);
            const sumUrl = `${API_BASE_URL}/api/archive/polls/${latest.poll_id}/summary?${ts()}`;
            const s = await fetchJson(sumUrl);
            setSummary(s);
            // Load evidence for all 6 candidates
            const firstSix = (p?.candidates || []).slice(0,6);
            for (const c of firstSix) {
              if (!alive) break;
              try {
                const evUrl = `${API_BASE_URL}/api/archive/evidence?scene_id=${encodeURIComponent(c.scene_id)}&${ts()}`;
                const ev = await fetchJson(evUrl);
                if (alive && ev) {
                  setPrefetch(prev => ({ ...prev, [c.scene_id]: ev }));
                }
                await new Promise(r => setTimeout(r, 100));
              } catch (e) {
                console.error('[Immortalize] Evidence fetch failed for scene', c.scene_id);
              }
            }
          } else {
            setPoll(null);
            setSummary(null);
          }
        } catch (e) {
          if (alive) {
            setPoll(null); setSummary(null);
            // show failing endpoint in banner
            setError(`Poll unavailable — latest/summary fetch failed (${API_BASE_URL}/api/archive/...)`);
          }
        }
        // Minted list should always render even if poll failed; seed with featured for instant paint
        const seedItems = Array.isArray(featuredSeed?.items) ? featuredSeed.items : Array.isArray(featuredSeed) ? featuredSeed : [];
        setMinted(seedItems.slice(0,3));
        try {
          // Prefer DB-backed minted list, fallback to legacy manifest
          // Use a larger window so real Polygon mints are not crowded out by recent mock entries
          const dbList = await fetchJson(`${API_BASE_URL}/api/archive/minted?limit=50&${ts()}`).catch((e) => {
            console.error('[Immortalize] Failed to fetch minted list:', e);
            return { items: [] };
          });
          let items = Array.isArray(dbList.items) ? dbList.items : [];
          if (!items.length) {
            const mani = await fetchJson(`${API_BASE_URL}/api/archive/minted/manifest?${ts()}`).catch((e) => {
              console.error('[Immortalize] Failed to fetch manifest:', e);
              return { items: [] };
            });
            items = Array.isArray(mani.items) ? mani.items : [];
          }
          if (items.length) {
            console.log('[Immortalize] Loaded', items.length, 'minted items');
            setMinted(items);
          } else {
            console.warn('[Immortalize] No minted items found');
          }
        } catch (e) {
          console.error('[Immortalize] Error loading minted items:', e);
        }
        // Load avatars (for Control Room parity)
        try {
          const djA = await fetchJson(`${API_BASE_URL}/api/ui/chat/avatars?${ts()}`).catch(() => ({}));
          setAvatars(djA || {});
        } catch {}
        // Defer scoreboard (non-critical) to avoid blocking first paint
        setTimeout(async () => {
          try {
            const d = await fetchJson(`${API_BASE_URL}/api/insights/scoreboard?window_hours=72&limit=12&${ts()}`);
            const tops = Array.isArray(d?.top_posts) ? d.top_posts : [];
            // Prefer author+content signature so replies on the same thread don't collapse
            const mapped = tops.map(p => {
              const sig = `${p?.author||''}|${String(p?.content||'').slice(0,200)}`;
              const key = sig.trim() ? sig : String(p?.scene_id || p?.thread_id || p?.url || Date.now()+Math.random());
              return { ...p, __key: key };
            });
            // Augment with one non-webhook post from current scene candidates (treat scene posts as independent post candidates)
            const fromScenes = [];
            try {
              const cand = Array.isArray(poll?.candidates) ? poll.candidates.slice(0, 6) : [];
              for (const c of cand) {
                const ev = prefetch[c.scene_id];
                const posts = Array.isArray(ev?.posts) ? ev.posts : [];
                const sourceUrl = ev?.source?.bsky_url || null;
                const firstReal = posts.find(pp => {
                  const a = String(pp?.author||'').toLowerCase();
                  return a && !a.includes('webhook') && !a.startsWith('bitbard');
                });
                if (firstReal) {
                  const sig = `${firstReal.author||''}|${String(firstReal.content||firstReal.text||'').slice(0,200)}`;
                  fromScenes.push({ author: firstReal.author, timestamp: firstReal.timestamp||null, content: firstReal.content||firstReal.text||'', url: null, __key: sig, __sourceUrl: sourceUrl });
                }
              }
            } catch {}
            // Merge and dedupe by __key
            // Prefer scene-derived posts (which carry __sourceUrl) so the source link is preserved after de-dup
            const combined = [...fromScenes, ...mapped];
            const seen = new Set();
            const uniq = [];
            for (const it of combined) {
              const k = it.__key;
              if (!k || seen.has(k)) continue;
              seen.add(k);
              uniq.push(it);
            }
            setPostCandidates(uniq.slice(0, 8));
          } catch {}
        }, 2500);
      } catch (e) {
        console.error('Immortalize load failed', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    const t = setInterval(async () => {
      try {
        if (!poll || !poll.poll_id) return;
        const s = await fetchJson(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/summary?${ts()}`, { retries: 3 });
        setSummary(s);
      } catch {}
    }, 45000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount; poll/prefetch state accessed via closure in interval

  const [selectedSceneId, setSelectedSceneId] = useState('');
  const [selectedPostKey, setSelectedPostKey] = useState('');

  // Restore vote selections from server (authoritative) on page load
  useEffect(() => {
    if (poll?.poll_id) {
      const voterId = (() => {
        try {
          const key = `poll_voter_id_${poll.poll_id}`;
          let vid = localStorage.getItem(key);
          if (!vid) {
            vid = `voter_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            localStorage.setItem(key, vid);
          }
          return vid;
        } catch {
          return `voter_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        }
      })();
      
      // Fetch voter's existing votes from server
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/voter/${voterId}?${Date.now()}`);
          if (response.ok) {
            const data = await response.json();
            console.log('[Immortalize] Restored votes from server:', data);
            if (data.scene_id) setSelectedSceneId(data.scene_id);
            if (data.post_key) setSelectedPostKey(data.post_key);
          }
        } catch (e) {
          console.error('[Immortalize] Failed to restore votes from server:', e);
          // Fallback to localStorage
          const savedScene = localStorage.getItem(`poll_vote_${poll.poll_id}`);
          const savedPost = localStorage.getItem(`poll_vote_post_${poll.poll_id}`);
          if (savedScene) setSelectedSceneId(savedScene);
          if (savedPost) setSelectedPostKey(savedPost);
        }
      })();
    }
  }, [poll?.poll_id]);

  // Generate or retrieve voter ID (localStorage-based deduplication)
  const getVoterId = () => {
    try {
      const key = `poll_voter_id_${poll?.poll_id || 'default'}`;
      let vid = localStorage.getItem(key);
      if (!vid) {
        vid = `voter_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem(key, vid);
      }
      return vid;
    } catch {
      return `voter_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }
  };

  const pollClosed = String(poll?.status || '').toLowerCase() === 'closed';

  const onVote = async (scene_id) => {
    if (!poll || !poll.poll_id || pollClosed) return;
    const voterId = getVoterId();
    const voteKey = `poll_vote_${poll.poll_id}`;
    const existingVote = localStorage.getItem(voteKey);
    
    // Toggle voting: if already voted for this scene, clear it; otherwise set it
    const isToggleOff = existingVote === scene_id;
    const newVote = isToggleOff ? null : scene_id;
    
    // Optimistic UI
    setSelectedSceneId(newVote);
    
    try {
      if (!isToggleOff) {
        // Voting for a scene
        await fetch(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/vote?${ts()}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scene_id, voter_id: voterId })
        });
        localStorage.setItem(voteKey, scene_id);
        // Track poll participation
        if (window.gtag) {
          window.gtag('event', 'poll_participation', {
            scene_id: scene_id,
            poll_id: poll.poll_id
          });
        }
      } else {
        // Un-voting (clearing the vote)
        localStorage.removeItem(voteKey);
      }
      // Refresh summary after change
      const r = await fetch(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/summary?${ts()}`);
      if (r.ok) setSummary(await r.json());
    } catch (e) {
      console.error('Vote error:', e);
      // Revert optimistic UI on error
      setSelectedSceneId(existingVote || null);
    }
  };

  const postKey = (p) => (
    p?.post_id || p?.__key || p?.scene_id || p?.thread_id || p?.url || String(p?.content||'').slice(0,120)
  );

  // status banner
  const StatusBar = () => (
    <div style={{ position: 'sticky', top: 0, zIndex: 1, fontSize: 11, opacity: .75, marginBottom: 6 }}>
      API: {API_BASE_URL} · source-ready{/* Only show poll error even if minted renders */}
      {error ? <span style={{ color: '#ff6b6b' }}> • {error}</span> : null}
    </div>
  );

  const leadingId = summary && summary.leading_scene_id;

  const Countdown = ({ targetIso }) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
    try {
      const t = new Date(targetIso).getTime();
      const diff = Math.max(0, t - now);
      const hh = Math.floor(diff / 3600000);
      const mm = Math.floor((diff % 3600000) / 60000);
      const ss = Math.floor((diff % 60000) / 1000);
      return <span>{`${hh.toString().padStart(2,'0')}:${mm.toString().padStart(2,'0')}:${ss.toString().padStart(2,'0')}`}</span>;
    } catch { return <span />; }
  };

  const avatarFor = (name='?') => {
    const n = String(name || '?').trim();
    // Remove emoji and extra characters for matching, normalize underscores to spaces
    const cleanName = n.replace(/[🎭🍷🗡🎪🎨]/g, '').replace(/_/g, ' ').trim();
    const key = cleanName.toLowerCase();
    // Try exact or prefix matches used in Control Room (case-insensitive)
    if (key.startsWith('bitbard') && avatars['bitbard']) return { url: avatars['bitbard'] };
    if (key.startsWith('lady macbeth') && avatars['lady macbeth']) return { url: avatars['lady macbeth'] };
    if (key.startsWith('sir andrew')) {
      if (avatars['sir andrew']) return { url: avatars['sir andrew'] };
      if (avatars['sir andrew aguecheek']) return { url: avatars['sir andrew aguecheek'] };
    }
    // Fuzzy contains match for Sir Andrew if keys differ slightly
    if (key.includes('sir andrew')) {
      const found = Object.keys(avatars || {}).find(k => k.toLowerCase().includes('sir andrew'));
      if (found && avatars[found]) return { url: avatars[found] };
    }
    if (key.startsWith('falstaff') && avatars['falstaff']) return { url: avatars['falstaff'] };
    if (key.startsWith('miranda') && avatars['miranda']) return { url: avatars['miranda'] };
    if (key.startsWith('iago') && avatars['iago']) return { url: avatars['iago'] };
    // Fallback: initials + color
    const parts = cleanName.split(/\s+/);
    const initials = (parts[0]?.[0] || '?') + (parts[1]?.[0] || '');
    // simple color hash
    let h = 0; for (let i=0;i<cleanName.length;i++) h = (h*31 + cleanName.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    const bg = `hsl(${hue} 45% 22%)`;
    return { initials: initials.toUpperCase(), bg };
  };

  const PostPreview = ({ post }) => {
    if (!post || !post.content) {
      return null;
    }
    const av = avatarFor(post.author || '?');
    return (
      <div className="line" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '6px 0' }}>
        {av.url ? (
          <img 
            src={av.url} 
            alt={post.author || ''} 
            style={{ 
              width: 28, 
              height: 28, 
              borderRadius: '50%', 
              flex: '0 0 auto', 
              objectFit: 'cover',
              backgroundColor: '#1a2d3d'
            }} 
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div style={{ 
          width: 28, 
          height: 28, 
          borderRadius: '50%', 
          background: av.bg || '#2a3d4d', 
          color: '#e6eef6', 
          display: av.url ? 'none' : 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          fontSize: 11, 
          flex: '0 0 auto',
          fontWeight: 500
        }}>{av.initials || '?'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#9bdcff', marginBottom: 2 }}>
            {post.author || 'Unknown'} {post.timestamp ? <span style={{ color: '#7aa4c9', fontSize: 11 }}>· {new Date(post.timestamp).toLocaleString()}</span> : null}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.35, wordWrap: 'break-word' }}>{post.content}</div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <StatusBar />
      <div className="line" style={{ opacity: .85, marginBottom: 8 }}>
        Vote here for scenes and posts worth preserving. Once a month, selected work is inscribed on Arweave and issued as editions on Polygon.
      </div>
      {loading && (
        <div className="line loading-pink" aria-live="polite">
          Loading<span className="loading-dots"><span className="loading-dot">.</span><span className="loading-dot">.</span><span className="loading-dot">.</span></span>
        </div>
      )}
      {(!loading && poll) && (
        <div>
          <div className="line" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ opacity: .9 }}>
              {String(poll.status || '').toLowerCase() === 'closed'
                ? 'This poll is closed. Browse candidates below; voting is paused until the next ballot opens.'
                : <>Poll closes in: <Countdown targetIso={poll.closes_at} /></>}
            </div>
            <div style={{ fontSize: 12, opacity: .8 }}>
              Poll: {poll.poll_id} · {String(poll.status || '').toLowerCase() === 'closed' ? 'Closed' : 'Closes'} {new Date(poll.closes_at).toLocaleString('en-US', { timeZone: 'America/Denver' })} MT
            </div>
          </div>
          {/* Mode toggle (posts later) */}
          <div className="line" style={{ marginBottom: 10 }}>
            <button onClick={() => setMode('scenes')} className={mode==='scenes' ? 'active' : ''} style={{ marginRight: 8, padding: '4px 10px', background: 'linear-gradient(180deg, #173049 0%, #0f2538 100%)', border: '1px solid rgba(255,255,255,0.15)' }}>Scenes</button>
            <button onClick={() => setMode('posts')} style={{ padding: '4px 10px', background: 'linear-gradient(180deg, #173049 0%, #0f2538 100%)', border: '1px solid rgba(255,255,255,0.15)', opacity: mode==='posts' ? 1 : 0.9 }}>Posts</button>
          </div>
          {mode === 'scenes' && (
          <div className="line scenes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))', gap: 16 }}>
            {(poll.candidates || []).slice(0, 6).map((c, i) => (
              <div key={i} style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #0f2436 0%, #0b1a29 100%)', minHeight: '200px' }}>
                {/* Current favorite badge */}
                {leadingId === c.scene_id && (
                  <div style={{ position: 'absolute', transform: 'translateY(-10px)', background: '#14324a', color: '#9bdcff', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>Current favorite</div>
                )}
                {/* Scene preview with full cue context */}
                <div className="line" style={{ marginBottom: 8 }}>
                  {(() => {
                    const ev = prefetch[c.scene_id];
                    const src = ev?.source?.bsky_url || c.bsky_url;
                    const cueCtx = ev?.cue_context;
                    
                    // Show Bluesky link if available
                    if (src) {
                      return (
                        <div className="line" style={{ fontSize: 12, marginBottom: 6 }}>
                          <a href={src} target="_blank" rel="noreferrer" style={{ color: '#9bdcff' }}>View on Bluesky</a>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {(() => {
                    const ev = prefetch[c.scene_id];
                    let allPosts = Array.isArray(ev?.posts) ? ev.posts : [];
                    const cueCtx = ev?.cue_context;
                    
                    // Sort by timestamp so incipit (earliest) is first and curtain (latest) is last
                    const ts = (p) => p?.timestamp ? new Date(p.timestamp).getTime() : 0;
                    allPosts = [...allPosts].sort((a, b) => ts(a) - ts(b));
                    
                    // Separate BitBard posts from character responses (order preserved from sort)
                    const isBitBardPost = (p) => {
                      const author = String(p?.author || '').toLowerCase();
                      return author && (author.includes('bitbard') || author.includes('webhook'));
                    };
                    const bitbardPosts = allPosts.filter(isBitBardPost);
                    const characterPosts = allPosts.filter(p => !isBitBardPost(p));
                    const incipitPost = bitbardPosts[0]; // Earliest BitBard = incipit
                    
                    // Show loading state if no posts yet
                    if (allPosts.length === 0) {
                      return (
                        <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.9 }}>
                          <div style={{ marginBottom: 4, fontWeight: 500 }}>Scene {c.scene_id}</div>
                          {c.incipit ? (
                            <div style={{ opacity: 0.8, fontStyle: 'italic', marginBottom: 8 }}>
                              <span style={{ fontSize: 11, color: '#9bdcff' }}>Cue:</span> {c.incipit.slice(0, 180)}{c.incipit.length > 180 ? '...' : ''}
                            </div>
                          ) : (
                            <div style={{ opacity: 0.7, fontStyle: 'italic' }}>Loading scene content...</div>
                          )}
                          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                            {c.posts ? `${c.posts} posts` : ''} {c.posts && c.score ? '·' : ''} {c.score ? `Score: ${c.score.toFixed(2)}` : ''}
                          </div>
                        </div>
                      );
                    }
                    
                    // Decide what to show based on expand state
                    const shownPosts = showFull[c.scene_id] ? allPosts : characterPosts.slice(0, 3);
                    
                    return (
                      <div>
                        {/* Show BitBard incipit as proper post (when NOT expanded) */}
                        {!showFull[c.scene_id] && incipitPost && (
                          <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <PostPreview post={{
                              author: incipitPost.author,
                              content: incipitPost.content || incipitPost.text,
                              timestamp: incipitPost.timestamp
                            }} />
                          </div>
                        )}
                        
                        {/* Pendant hero image */}
                        {!showFull[c.scene_id] && c.pendant_url && (
                          <div style={{ marginBottom: 10, borderRadius: 6, overflow: 'hidden' }}>
                            <img src={c.pendant_url} alt="" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }} loading="lazy" />
                          </div>
                        )}

                        {/* Show target card for promote cues (when NOT expanded) */}
                        {!showFull[c.scene_id] && cueCtx?.target_card && (
                          <div style={{ 
                            marginBottom: 12, 
                            padding: 10, 
                            background: 'rgba(155, 220, 255, 0.05)', 
                            border: '1px solid rgba(155, 220, 255, 0.15)',
                            borderRadius: 6
                          }}>
                            <div style={{ fontSize: 11, color: '#9bdcff', marginBottom: 6, fontWeight: 500 }}>
                              Responding to: {cueCtx.target_card.author}
                            </div>
                            {cueCtx.target_card.image && (
                              <img 
                                src={cueCtx.target_card.image} 
                                alt=""
                                style={{ 
                                  width: '100%', 
                                  maxHeight: 160, 
                                  objectFit: 'cover',
                                  objectPosition: 'top',
                                  borderRadius: 4, 
                                  marginBottom: 8 
                                }}
                              />
                            )}
                            {cueCtx.target_card.title && (
                              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                                {cueCtx.target_card.title}
                              </div>
                            )}
                            {cueCtx.target_card.description && (
                              <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.4, marginBottom: 6 }}>
                                {cueCtx.target_card.description}
                              </div>
                            )}
                            {cueCtx.target_card.url && (
                              <a 
                                href={cueCtx.target_card.url} 
                                target="_blank" 
                                rel="noreferrer"
                                style={{ fontSize: 11, color: '#9bdcff' }}
                              >
                                View original post →
                              </a>
                            )}
                          </div>
                        )}
                        
                        {/* Show source card from shortlist (news links, images from cue) when NOT expanded */}
                        {!showFull[c.scene_id] && !cueCtx?.target_card && c.source_card && (
                          <div style={{ 
                            marginBottom: 12, 
                            padding: 10, 
                            background: 'rgba(155, 220, 255, 0.05)', 
                            border: '1px solid rgba(155, 220, 255, 0.15)',
                            borderRadius: 6,
                            cursor: c.source_card.uri ? 'pointer' : 'default'
                          }}
                          onClick={() => c.source_card.uri && window.open(c.source_card.uri, '_blank')}
                          >
                            {c.source_card.thumb && (
                              <img 
                                src={c.source_card.thumb} 
                                alt=""
                                style={{ 
                                  width: '100%', 
                                  maxHeight: 160, 
                                  objectFit: 'cover',
                                  objectPosition: 'top',
                                  borderRadius: 4, 
                                  marginBottom: 8 
                                }}
                              />
                            )}
                            {c.source_card.title && (
                              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                                {c.source_card.title}
                              </div>
                            )}
                            {c.source_card.description && (
                              <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
                                {c.source_card.description.slice(0, 120)}{c.source_card.description.length > 120 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show character responses or all posts when expanded */}
                        {shownPosts.map((p, idx) => {
                          const post = {
                            author: p.author || '',
                            content: p.content || p.text || '',
                            timestamp: p.timestamp || null
                          };
                          return <PostPreview key={`scene-${c.scene_id}-post-${idx}`} post={post} />;
                        })}
                        
                        {/* Expand button */}
                        {(characterPosts.length > 3 || allPosts.length > characterPosts.length) && (
                          <div className="line" style={{ marginTop: 8 }}>
                            <button
                              onClick={() => setShowFull(s => ({ ...s, [c.scene_id]: !s[c.scene_id] }))}
                              style={{ fontSize: 12, padding: '6px 12px', background: 'linear-gradient(180deg, #173049 0%, #0f2538 100%)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4 }}
                            >
                              {showFull[c.scene_id] ? 'Show less' : `Show full scene (${allPosts.length} posts)`}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {/* CTAs */}
                <div style={{ marginTop: 'auto', display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => onVote(c.scene_id)}
                    disabled={pollClosed}
                    style={{
                      flex: 1,
                      padding: '4px 10px',
                      background: (selectedSceneId === c.scene_id) ? 'linear-gradient(180deg, #5a1e47 0%, #421732 100%)' : 'linear-gradient(180deg, #173049 0%, #0f2538 100%)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      cursor: pollClosed ? 'not-allowed' : 'pointer',
                      opacity: pollClosed ? 0.55 : 1
                    }}
                  >
                    { pollClosed ? 'Closed' : (selectedSceneId === c.scene_id ? 'Voted ✓' : 'Vote') }
                  </button>
                </div>
              </div>
            ))}
          </div>
          )}
          {mode === 'posts' && (
            <div className="line" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))', gap: 16 }}>
              {((poll?.post_candidates || postCandidates || [])).slice(0,6).map((p, i) => (
                <div key={i} style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 12px', background: 'linear-gradient(180deg, #0f2436 0%, #0b1a29 100%)' }}>
                  {/* Parent card - show what they were replying to */}
                  {(p.parent_text || p.parent_author) ? (
                    <div style={{ 
                      marginBottom: 12, 
                      padding: 10, 
                      background: 'rgba(155, 220, 255, 0.05)', 
                      border: '1px solid rgba(155, 220, 255, 0.15)',
                      borderRadius: 6
                    }}>
                      <div style={{ fontSize: 11, color: '#9bdcff', marginBottom: 6, fontWeight: 500 }}>
                        Replying to: @{p.parent_author || 'unknown'}
                      </div>
                      {p.parent_text && (
                        <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.4 }}>
                          "{p.parent_text.slice(0, 180)}{p.parent_text.length > 180 ? '...' : ''}"
                        </div>
                      )}
                    </div>
                  ) : p.__sourceUrl ? (
                    <div className="line" style={{ fontSize: 12, marginBottom: 6 }}>
                      <a href={p.__sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#9bdcff' }}>Responding to: view target card</a>
                    </div>
                  ) : null}
                  <PostPreview post={{ author: p.author, timestamp: p.timestamp || null, content: p.content }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                        onClick={async () => {
                        const key = postKey(p);
                        const voteKeyPost = `poll_vote_post_${poll?.poll_id || 'default'}`;
                        const existingVote = localStorage.getItem(voteKeyPost);
                        
                        // Toggle voting: if already voted for this post, clear it; otherwise set it
                        const isToggleOff = existingVote === key;
                        
                        if (!isToggleOff) {
                          // Voting for a post
                          setSelectedPostKey(key);
                          try {
                            if (poll && poll.poll_id && key) {
                              const voterId = getVoterId();
                              await fetch(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/vote?${ts()}` , {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ post_key: key, voter_id: voterId })
                              });
                              localStorage.setItem(voteKeyPost, key);
                              // Refresh summary
                              const r = await fetch(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/summary?${ts()}`);
                              if (r.ok) setSummary(await r.json());
                            }
                          } catch (e) {
                            console.error('Post vote error:', e);
                            setSelectedPostKey('');
                          }
                        } else {
                          // Un-voting (clearing the vote)
                          setSelectedPostKey('');
                          localStorage.removeItem(voteKeyPost);
                          // Refresh summary
                          const r = await fetch(`${API_BASE_URL}/api/archive/polls/${poll.poll_id}/summary?${ts()}`);
                          if (r.ok) setSummary(await r.json());
                        }
                      }}
                      disabled={pollClosed}
                      style={{
                        flex: 1,
                        padding: '4px 10px',
                        background: (selectedPostKey === postKey(p)) ? 'linear-gradient(180deg, #5a1e47 0%, #421732 100%)' : 'linear-gradient(180deg, #173049 0%, #0f2538 100%)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        cursor: pollClosed ? 'not-allowed' : 'pointer',
                        opacity: pollClosed ? 0.55 : 1
                      }}
                    >
                      { pollClosed ? 'Closed' : (selectedPostKey === postKey(p) ? 'Voted ✓' : 'Vote') }
                    </button>
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Open</a> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="line" style={{ marginTop: 18, color: '#9bdcff', fontWeight: 700, fontSize: 16 }}>Previously Minted</div>
      <div className="line" style={{ height: 6 }} />
      <div className="line minted-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))', gap: 16 }}>
        {(() => {
          const sorted = (minted || []).slice()
            .filter(x => x.chain === 'polygon' || x.chain === 'arweave')
            .sort((a,b)=> new Date(b.inscribed_at||b.minted_at||b.issued_at||0)-new Date(a.inscribed_at||a.minted_at||a.issued_at||0));
          if (!sorted.length) return (
            <div style={{ opacity: 0.7 }}>No minted items recorded yet.</div>
          );
          
          // Debug: log what we're working with
          console.log('[Immortalize] Total minted items from API:', minted?.length || 0);
          console.log('[Immortalize] After filter (polygon/arweave only):', sorted.length);
          console.log('[Immortalize] Items being displayed:', sorted.map(x => ({ scene_id: x.scene_id, chain: x.chain, kind: x.kind })));
          
          // Find latest scene and post by comparing dates (not just first match)
          const scenes = sorted.filter(x => !(x.kind==='post' || String(x.scene_id||'').startsWith('post:')));
          const posts = sorted.filter(x => (x.kind==='post' || String(x.scene_id||'').startsWith('post:')));
          const latestScene = scenes.length > 0 ? scenes[0] : null;
          const latestPost = posts.length > 0 ? posts[0] : null;
          const latestSceneId = latestScene ? String(latestScene.scene_id || '') : '';
          const latestPostId = latestPost ? String(latestPost.scene_id || '') : '';
          
          const cards = sorted.slice(0, 9);
          return cards.map((m, i) => {
            const contract = m.contract || '';
            const tokenIds = Array.isArray(m.token_ids) ? m.token_ids : [];
            const firstToken = tokenIds.length > 0 ? tokenIds[0] : null;
            // Only show NFT link if we have a contract, token IDs, and the first token is valid (> 0)
            // Hide NFT links for incomplete mints (empty token_ids or chain='arweave' with no tokens)
            const isIncomplete = tokenIds.length === 0 || firstToken === null || firstToken <= 0;
            const openSeaToken = (!isIncomplete && contract && firstToken > 0) ? `https://opensea.io/assets/polygon/${contract}/${firstToken}` : null;
            const openSeaCollection = contract ? `https://opensea.io/assets/polygon/${contract}` : null;
                const readSceneRaw = m.animation_url || m.viewer_url || null;
            const readScene = (readSceneRaw && String(readSceneRaw).startsWith('/')) ? `${API_BASE_URL}${readSceneRaw}` : (readSceneRaw && String(readSceneRaw).startsWith('http')) ? readSceneRaw : null;
            return (
              <div key={i} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '16px 16px', background: 'linear-gradient(180deg, #0f2436 0%, #0b1a29 100%)' }}>
                {((m.kind==='post' || String(m.scene_id||'').startsWith('post:')) && String(m.scene_id)===latestPostId) ? (
                  <div className="line loading-pink" style={{ fontSize: 12, marginBottom: 6, fontWeight: 700 }}>Recent Winner 🎉</div>
                ) : ((!(m.kind==='post' || String(m.scene_id||'').startsWith('post:')) && String(m.scene_id)===latestSceneId) ? (
                  <div className="line loading-pink" style={{ fontSize: 12, marginBottom: 6, fontWeight: 700 }}>Recent Winner 🎉</div>
                ) : null)}
                <div className="line" style={{ marginBottom: 6, opacity: .95, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} title={m.scene_id}>
                  <span>{m.display_title || (m.kind === 'post' ? 'Immortalized Post' : 'Immortalized Scene')}</span>
                  {(() => {
                    const rx = Number(m.reactions || m.reaction_count || 0);
                    if (!rx || rx <= 0) return null;
                    return (
                      <span style={{ fontSize: 11, color: '#ffb2e4', background: 'rgba(255,178,228,0.14)', border: '1px solid rgba(255,178,228,0.35)', padding: '1px 6px', borderRadius: 10, marginLeft: 8 }}>♥ {rx}</span>
                    );
                  })()}
                </div>
                {(m.minted_at || m.inscribed_at) ? (
                  <div className="line" style={{ fontSize: 12, opacity: .8, marginBottom: 8 }}>
                    {m.minted_at ? 'Minted:' : 'Inscribed:'} {new Date(m.minted_at || m.inscribed_at).toLocaleString('en-US', { timeZone: 'America/Denver' })} MT
                  </div>
                ) : null}
                {/* Pendant hero image (if scene had one attached on Bluesky) */}
                {m.pendant_url ? (
                  <div style={{ marginBottom: 10, borderRadius: 6, overflow: 'hidden' }}>
                    <img src={m.pendant_url} alt="" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }} loading="lazy" />
                  </div>
                ) : null}
                <MintedPreview sceneId={m.scene_id} avatars={avatars} forceBitBard={!(m.kind==='post' || String(m.scene_id||'').startsWith('post:'))} incipitFallback={m.incipit || ''} authorFallback={m.incipit_author || ''} />
                <div className="line" style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
                  {openSeaToken ? <a href={openSeaToken} target="_blank" rel="noreferrer">View NFT</a> : (openSeaCollection ? <a href={openSeaCollection} target="_blank" rel="noreferrer">OpenSea</a> : null)}
                  {readScene ? <a href={readScene} target="_blank" rel="noreferrer">View snapshot</a> : null}
                </div>
              </div>
            );
          });
        })()}
      </div>

    </div>
  );
}

function MintedPreview({ sceneId, avatars, forceBitBard, incipitFallback, authorFallback }) {
  // Use fallback data directly - no API call needed since incipit/author are already in minted response
  // This eliminates 13+ API calls when loading the Immortalize tab
  const name = authorFallback || 'BitBard';
  let displayText = String(incipitFallback || '').trim();
  
  // Skip if no meaningful content
  if (!displayText && !name) return null;
  
  // Truncate long text
  if (displayText.length > 220) displayText = displayText.slice(0, 217) + '…';
  
  const key = String(name || '').toLowerCase();
  let avUrl = null;
  if (forceBitBard) {
    avUrl = avatars?.['bitbard'] || null;
  } else {
    // Robust avatar resolution for Sir Andrew and others
    if (key.includes('sir andrew')) {
      avUrl = avatars?.['sir andrew'] || avatars?.['sir andrew aguecheek'] || null;
      if (!avUrl) {
        const found = Object.keys(avatars || {}).find(k => k.toLowerCase().includes('sir andrew'));
        if (found) avUrl = avatars[found];
      }
    }
    if (!avUrl) {
      avUrl = avatars?.[key] || null;
    }
    if (!avUrl && key.startsWith('lady macbeth')) avUrl = avatars?.['lady macbeth'] || null;
    if (!avUrl && key.startsWith('falstaff')) avUrl = avatars?.['falstaff'] || null;
    if (!avUrl && key.startsWith('miranda')) avUrl = avatars?.['miranda'] || null;
    if (!avUrl && key.startsWith('iago')) avUrl = avatars?.['iago'] || null;
    if (!avUrl) avUrl = avatars?.['bitbard'] || null;
  }
  const fallbackAvatar = { initials: (name[0]||'?').toUpperCase(), bg: '#21344a' };
  
  return (
    <div className="line" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      {avUrl ? (
        <span className="avatar sm" style={{ backgroundImage: `url(${avUrl})`, width: 28, height: 28, borderRadius: 20, flex: '0 0 auto', backgroundSize: 'cover', backgroundPosition: 'center' }} />
      ) : (
        <div style={{ width: 28, height: 28, borderRadius: 20, background: fallbackAvatar.bg, color: '#e6eef6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flex: '0 0 auto' }}>{fallbackAvatar.initials}</div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: '#9bdcff' }}>{name}</div>
        <div style={{ fontSize: 14, lineHeight: 1.35 }}>{displayText}</div>
      </div>
    </div>
  );
}


